/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */

import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Button, Input, Form, Toggle, IconButton, Message, SelectPicker } from 'rsuite';
import { MdAdd, MdDelete, MdDragIndicator } from 'react-icons/md';
import styled from 'styled-components';
import api from '../../../services/api';
import { ResponsiveForm } from '../../../components/ResponsiveForm';

const AlternativasContainer = styled.div`
  margin-top: 16px;
`;

const AlternativaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  margin-bottom: 8px;
  background: #f9f9f9;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.correct ? '#4caf50' : '#e0e0e0')};

  .rs-theme-dark & {
    background: #2a2a2a;
    border-color: ${(props) => (props.correct ? '#4caf50' : '#444')};
  }

  .drag-handle {
    cursor: grab;
    color: #999;
    padding: 4px;

    &:active {
      cursor: grabbing;
    }
  }

  .alternativa-input {
    flex: 1;
  }

  .correct-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: ${(props) => (props.correct ? '#4caf50' : '#888')};
  }
`;

const AddAlternativaButton = styled.button`
  width: 100%;
  padding: 12px;
  border: 2px dashed #ccc;
  border-radius: 8px;
  background: transparent;
  color: #888;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    border-color: #3b82f6;
    color: #3b82f6;
  }
`;

const FeedbackSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;

  .rs-theme-dark & {
    border-color: #444;
  }

  h4 {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 16px;
    color: #333;

    .rs-theme-dark & {
      color: #ccc;
    }
  }
`;

const QuestionEditor = ({
  open,
  onClose,
  onSave,
  initialData = null,
  loading = false,
}) => {
  const [enunciado, setEnunciado] = useState(initialData?.enunciado || '');
  const [feedbackAcerto, setFeedbackAcerto] = useState(initialData?.feedback_acerto || '');
  const [feedbackErro, setFeedbackErro] = useState(initialData?.feedback_erro || '');
  const [alternativas, setAlternativas] = useState(
    initialData?.alternativas || [
      { texto: '', correta: true, ordem: 1 },
      { texto: '', correta: false, ordem: 2 },
    ]
  );
  const [errors, setErrors] = useState({});
  const [categoriaQuestao, setCategoriaQuestao] = useState(initialData?.id_categoria || 2);
  const [categoriasQuestoes, setCategoriasQuestoes] = useState([]);

  const buscarCategoriasQuestoes = useCallback(async () => {
    try {
      const resposta = await api.get('categorias-questoes');
      setCategoriasQuestoes(resposta.data.categorias);
    } catch (error) {
      console.error('Erro ao buscar categorias de questões:', error);
    }
  }, []);

  useEffect(() => {
    if (initialData) {
      setEnunciado(initialData.enunciado);
      setFeedbackAcerto(initialData.feedback_acerto);
      setFeedbackErro(initialData.feedback_erro);
      setAlternativas(initialData.alternativas);
      setCategoriaQuestao(initialData.id_categoria);
    }
      buscarCategoriasQuestoes();
  }, [initialData]);

  const handleAddAlternativa = useCallback(() => {
    setAlternativas((prev) => [
      ...prev,
      { texto: '', correta: false, ordem: prev.length + 1 },
    ]);
  }, []);

  const handleRemoveAlternativa = useCallback((index) => {
    setAlternativas((prev) => {
      if (prev.length <= 2) return prev;
      const newAlts = prev.filter((_, i) => i !== index);
      // Reordenar
      return newAlts.map((alt, i) => ({ ...alt, ordem: i + 1 }));
    });
  }, []);

  const handleAlternativaChange = useCallback((index, field, value) => {
    setAlternativas((prev) => {
      const newAlts = [...prev];
      
      if (field === 'correta' && value === true) {
        // Desmarcar todas as outras
        newAlts.forEach((alt, i) => {
          newAlts[i] = { ...alt, correta: i === index };
        });
      } else {
        newAlts[index] = { ...newAlts[index], [field]: value };
      }
      
      return newAlts;
    });
  }, []);

  const validate = useCallback(() => {
    const newErrors = {};

    if (!enunciado.trim()) {
      newErrors.enunciado = 'O enunciado é obrigatório';
    }

    if (alternativas.length < 2) {
      newErrors.alternativas = 'Mínimo de 2 alternativas';
    }

    const hasCorrect = alternativas.some((alt) => alt.correta);
    if (!hasCorrect) {
      newErrors.alternativas = 'Selecione uma alternativa correta';
    }

    const emptyAlts = alternativas.some((alt) => !alt.texto.trim());
    if (emptyAlts) {
      newErrors.alternativas = 'Preencha todas as alternativas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [enunciado, alternativas]);

  const handleSave = useCallback(() => {
    if (!validate()) return;

    const data = {
      enunciado,
      feedback_acerto: feedbackAcerto,
      feedback_erro: feedbackErro,
      alternativas: alternativas.map((alt, index) => ({
        ...alt,
        ordem: index + 1,
      })),
      id_categoria: categoriaQuestao || 2, // Usar categoria selecionada ou padrão
    };

    if (initialData?.id) {
      data.id = initialData.id;
    }
    onSave(data);

    // setEnunciado('');
    // setFeedbackAcerto('');
    // setFeedbackErro('');
    // setAlternativas([
    //   { texto: '', correta: true, ordem: 1 },
    //   { texto: '', correta: false, ordem: 2 },
    // ]);
    // setCategoriaQuestao(2);
    setErrors({});

  }, [enunciado, feedbackAcerto, feedbackErro, alternativas, initialData, onSave, validate, categoriaQuestao]);

  const handleClose = useCallback(() => {
    setEnunciado(initialData?.enunciado || '');
    setFeedbackAcerto(initialData?.feedback_acerto || '');
    setFeedbackErro(initialData?.feedback_erro || '');
    setAlternativas(
      initialData?.alternativas || [
        { texto: '', correta: true, ordem: 1 },
        { texto: '', correta: false, ordem: 2 },
      ]
    );
    setCategoriaQuestao(null);
    setErrors({});
    onClose();
  }, [initialData, onClose]);

  return (
    <Modal open={open} onClose={handleClose} size="md">
      <Modal.Header>
        <Modal.Title>
          {initialData ? 'Editar Questão' : 'Nova Questão'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form fluid>
          <Form.Group>
            <Form.ControlLabel>Categoria da Questão</Form.ControlLabel>
            <ResponsiveForm
              name="categoria"
              data={categoriasQuestoes.map((cat) => ({
                label: cat.nome,
                value: cat.id,
              }))}
              value={categoriaQuestao}
              onChange={(value) => setCategoriaQuestao(value)}
              placeholder="Selecione a categoria"
              accepter={SelectPicker}
            />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>Enunciado *</Form.ControlLabel>
            <Input
              as="textarea"
              rows={3}
              value={enunciado}
              onChange={setEnunciado}
              placeholder="Digite o enunciado da questão..."
            />
            {errors.enunciado && (
              <Message type="error" style={{ marginTop: 8 }}>
                {errors.enunciado}
              </Message>
            )}
          </Form.Group>

          <AlternativasContainer>
            <Form.ControlLabel>Alternativas * (marque a correta)</Form.ControlLabel>
            
            {alternativas.map((alt, index) => (
              <AlternativaRow key={index} correct={alt.correta}>
                <MdDragIndicator className="drag-handle" />
                
                <Input
                  className="alternativa-input"
                  value={alt.texto}
                  onChange={(value) => handleAlternativaChange(index, 'texto', value)}
                  placeholder={`Alternativa ${index + 1}`}
                />

                <div className="correct-toggle">
                  <Toggle
                    checked={alt.correta}
                    onChange={(checked) => handleAlternativaChange(index, 'correta', checked)}
                    checkedChildren="✓"
                    unCheckedChildren=""
                  />
                  {alt.correta && <span>Correta</span>}
                </div>

                <IconButton
                  icon={<MdDelete />}
                  appearance="subtle"
                  color="red"
                  size="sm"
                  onClick={() => handleRemoveAlternativa(index)}
                  disabled={alternativas.length <= 2}
                />
              </AlternativaRow>
            ))}

            {errors.alternativas && (
              <Message type="error" style={{ marginTop: 8, marginBottom: 8 }}>
                {errors.alternativas}
              </Message>
            )}

            <AddAlternativaButton onClick={handleAddAlternativa}>
              <MdAdd /> Adicionar Alternativa
            </AddAlternativaButton>
          </AlternativasContainer>

          <FeedbackSection>
            <h4>Feedback (opcional)</h4>
            
            <Form.Group>
              <Form.ControlLabel>Feedback de Acerto</Form.ControlLabel>
              <Input
                as="textarea"
                rows={2}
                value={feedbackAcerto}
                onChange={setFeedbackAcerto}
                placeholder="Mensagem exibida quando o usuário acerta..."
              />
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>Feedback de Erro</Form.ControlLabel>
              <Input
                as="textarea"
                rows={2}
                value={feedbackErro}
                onChange={setFeedbackErro}
                placeholder="Mensagem exibida quando o usuário erra..."
              />
            </Form.Group>
          </FeedbackSection>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleClose} appearance="subtle">
          Cancelar
        </Button>
        <Button onClick={handleSave} appearance="primary" loading={loading}>
          {initialData ? 'Salvar Alterações' : 'Criar Questão'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default QuestionEditor;
